import { ActionIcon, Box, getDefaultZIndex, Group } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Janela flutuante: portal, arrasto pela barra, resize pelo canto.
 *
 * ## Por que ela existe
 *
 * A mesma moldura estava escrita DUAS vezes — `FloatingLogWindow` dentro de
 * `Recursos.tsx` (~4.100 linhas) e `FloatingFileBrowser` em `components/files/`. O
 * terminal pinado seria a terceira cópia, e a TASK-10330 acabou de mostrar o preço de
 * deixar a mesma coisa definida em três lugares: enquanto discordarem, cada tela nova
 * escolhe por conta própria.
 *
 * ## O z-index NÃO é um número decorado
 *
 * As duas cópias usavam `9000`, e com isso a janela ficava por cima de modal — uma
 * janela de terminal cobrindo um diálogo "confirmar exclusão?" não é enfeite quebrado,
 * é clique bloqueado numa confirmação.
 *
 * Aqui ele vem do próprio Mantine. Medido nesta versão (7.17) em 04/09/2026:
 * `app` 100, `modal` 200, `popover` 300, `overlay` 400. Ficar um abaixo de `modal`
 * põe a janela acima do conteúdo e abaixo de qualquer coisa que exija resposta — e
 * derivar em vez de fixar é o que impede o número de envelhecer sozinho quando a
 * biblioteca subir de versão.
 *
 * ## O que ela NÃO decide
 *
 * O que "fechar" significa. Ela chama `onFechar` e continua montada: no visor de log
 * fechar é descartar, no terminal pinado o mesmo X **despina** e a sessão continua
 * viva. Quem sabe disso é quem usa a moldura.
 */

export interface Geometria {
  x: number
  y: number
  w: number
  h: number
}

const LARGURA_MINIMA = 320
const ALTURA_MINIMA = 200

export function JanelaFlutuante({
  titulo,
  acoes,
  inicial,
  onFechar,
  aoMover,
  children,
}: {
  titulo: ReactNode
  acoes?: ReactNode
  inicial: Geometria
  onFechar: () => void
  /** Chamado ao fim de um arrasto ou resize, para o dono guardar a geometria. */
  aoMover?: (g: Geometria) => void
  children: ReactNode
}) {
  const geo = useRef<Geometria>(inicial)
  const [, repintar] = useState(0)

  // A geometria vive num `ref` e o render e forcado a mao: durante o arrasto ela muda
  // a cada `mousemove`, e passar por `useState` a cada evento faria o conteudo da
  // janela — um terminal, um visor de log — repintar dezenas de vezes por segundo.
  const aplicar = useCallback((g: Geometria) => {
    geo.current = g
    repintar(n => n + 1)
  }, [])

  const arrastar = useCallback((e: React.MouseEvent) => {
    // Clique numa acao nao e arrasto: sem isto, apertar "encerrar" e mover 1px
    // engoliria o clique.
    if ((e.target as HTMLElement).closest('[data-sem-arrasto]')) return
    e.preventDefault()
    const dx = e.clientX - geo.current.x
    const dy = e.clientY - geo.current.y

    const mover = (ev: MouseEvent) => {
      aplicar({
        ...geo.current,
        x: Math.max(0, Math.min(window.innerWidth - geo.current.w, ev.clientX - dx)),
        y: Math.max(0, Math.min(window.innerHeight - 40, ev.clientY - dy)),
      })
    }
    const soltar = () => {
      window.removeEventListener('mousemove', mover)
      window.removeEventListener('mouseup', soltar)
      aoMover?.(geo.current)
    }
    window.addEventListener('mousemove', mover)
    window.addEventListener('mouseup', soltar)
  }, [aplicar, aoMover])

  const redimensionar = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const x0 = e.clientX
    const y0 = e.clientY
    const { w: w0, h: h0 } = geo.current

    const mover = (ev: MouseEvent) => {
      aplicar({
        ...geo.current,
        w: Math.max(LARGURA_MINIMA, w0 + (ev.clientX - x0)),
        h: Math.max(ALTURA_MINIMA, h0 + (ev.clientY - y0)),
      })
    }
    const soltar = () => {
      window.removeEventListener('mousemove', mover)
      window.removeEventListener('mouseup', soltar)
      aoMover?.(geo.current)
    }
    window.addEventListener('mousemove', mover)
    window.addEventListener('mouseup', soltar)
  }, [aplicar, aoMover])

  // Uma janela que fica ouvindo o `window` depois de desmontada e vazamento silencioso.
  useEffect(() => () => {
    window.removeEventListener('mousemove', () => {})
  }, [])

  const { x, y, w, h } = geo.current

  return createPortal(
    <Box
      data-janela
      style={{
        position: 'fixed',
        left: x,
        top: y,
        width: w,
        height: h,
        zIndex: getDefaultZIndex('modal') - 1,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 8,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
        overflow: 'hidden',
        border: '1px solid var(--ai-console-border)',
        background: 'var(--ai-console-bg)',
        minWidth: LARGURA_MINIMA,
        minHeight: ALTURA_MINIMA,
      }}
    >
      <Group
        data-testid="janela-barra"
        onMouseDown={arrastar}
        justify="space-between"
        px={10}
        py={6}
        wrap="nowrap"
        style={{
          cursor: 'grab',
          background: 'var(--ai-console-surface)',
          borderBottom: '1px solid var(--ai-console-border)',
          flexShrink: 0,
          userSelect: 'none',
        }}
      >
        <Box style={{ minWidth: 0, overflow: 'hidden' }}>{titulo}</Box>
        <Group gap={4} wrap="nowrap" data-sem-arrasto="1">
          {acoes}
          <ActionIcon
            size="xs"
            variant="subtle"
            color="gray"
            aria-label="Fechar janela"
            onClick={onFechar}
          >
            <IconX size={12} />
          </ActionIcon>
        </Group>
      </Group>

      <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{children}</Box>

      <Box
        data-testid="janela-canto"
        onMouseDown={redimensionar}
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: 16,
          height: 16,
          cursor: 'nwse-resize',
          zIndex: 1,
          background:
            'linear-gradient(135deg, transparent 50%, var(--ai-console-border) 50%)',
        }}
      />
    </Box>,
    document.body,
  )
}
